import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "../i18n";

const API = process.env.REACT_APP_BACKEND_URL;

/* Active special offers — landing pages flagged in Admin. Hidden entirely when none. */
export const SpecialOffers = () => {
  const { lang } = useLang();
  const [offers, setOffers] = useState(null);
  const ar = lang === "ar";
  const Arrow = ar ? ArrowLeft : ArrowRight;

  useEffect(() => {
    fetch(`${API}/api/offers`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setOffers)
      .catch(() => setOffers([]));
  }, []);

  if (!offers || offers.length === 0) return null;

  return (
    <section id="offers" data-testid="special-offers-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <p className="text-luxury-gold text-sm tracking-[0.35em] mb-6">{ar ? "عروض خاصة" : "SPECIAL OFFERS"}</p>
          <h2 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-luxury-bone leading-[1.25]">
            {ar ? "لفترة " : "For a "}<span className="gold-shimmer italic">{ar ? "محدودة" : "limited time"}</span>
          </h2>
        </motion.div>

        <div className={`grid grid-cols-1 ${offers.length > 1 ? "md:grid-cols-2" : "max-w-2xl mx-auto"} gap-6`}>
          {offers.map((o, i) => (
            <motion.div
              key={o.slug}
              data-testid={`offer-card-${i}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="group rounded-3xl overflow-hidden bg-luxury-gold/5 border border-luxury-gold/50 hover:border-luxury-gold hover:shadow-[0_20px_60px_rgba(201,164,92,0.18)] hover:-translate-y-2 transition-all duration-500"
            >
              {o.image && (
                <div className="relative h-44 md:h-52 overflow-hidden">
                  <img src={o.image} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(27,14,35,0.1), rgba(27,14,35,0.55))" }} />
                </div>
              )}
              <div className="p-8 md:p-10">
                <h3 className="font-serif text-2xl md:text-3xl text-luxury-bone mb-6">{o.title}</h3>
                <Link
                  to={`/p/${o.slug}`}
                  data-testid={`offer-cta-${i}`}
                  className="inline-flex items-center gap-3 bg-luxury-gold text-luxury-black font-bold px-8 py-4 rounded-full shadow-[0_12px_45px_-8px_rgba(209,160,92,0.55)] hover:bg-luxury-goldLight hover:shadow-[0_16px_60px_-6px_rgba(209,160,92,0.7)] hover:-translate-y-0.5 transition-[background-color,box-shadow,transform] duration-300"
                >
                  {ar ? "اكتشفي العرض" : "Discover the offer"}
                  <Arrow className="w-5 h-5" strokeWidth={1.5} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
